import type {ReactElement} from 'react';
import {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import type {IsUserEligibleResponse} from '@Components/social-media/social-media.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Popover} from '@Components/popover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {getAssetUrl} from '@Utils/s3.util';
import {
  CAPTION_TAGS_POPOVER_ALREADY_SHOWN,
  USER_HAS_SEEN_MAX_TAGS_POPOVERS,
} from '../../social-post-caption.types';
import type {CaptionTagsPopoverHandle, CaptionTagsPopoverProps} from './caption-tags-popover.types';
import styles from '../../social-post-caption.module.scss';

export const CaptionTagsPopover = forwardRef<CaptionTagsPopoverHandle, CaptionTagsPopoverProps>(function CaptionTagsPopover(
  {textAreaContainerRef, onEligibilityDetermined}: CaptionTagsPopoverProps,
  ref
): ReactElement | null {
  const [showTagsPopOver, setShowTagsPopOver] = useState<boolean>(false);
  const [isUserEligibleToSeeTagsPopOver, setIsUserEligibleToSeeTagsPopOver] = useState<boolean>(false);

  useEffect(() => {
    void checkCaptionTagsPopoverEligibility();
    setCaptionTagsPopoverUnseenInCache();
  }, []);

  const checkCaptionTagsPopoverEligibility = async (): Promise<void> => {
    const userHasSeenMaxTagsPopOvers = window.PMW.getCacheItem(USER_HAS_SEEN_MAX_TAGS_POPOVERS) as boolean;

    if (userHasSeenMaxTagsPopOvers) {
      setIsUserEligibleToSeeTagsPopOver(false);
      onEligibilityDetermined(false);
    } else {
      const result = (await window.PMW.readLocal('socialmedia/shouldUserSeeCaptionTagsPopover')) as IsUserEligibleResponse;
      setIsUserEligibleToSeeTagsPopOver(result?.isEligible);
      onEligibilityDetermined(result?.isEligible);

      if (!result?.isEligible) {
        window.PMW.setCacheItem(USER_HAS_SEEN_MAX_TAGS_POPOVERS, true, window.PMW.DEFAULT_CACHE_EXPIRY);
      }
    }
  };

  const setCaptionTagsPopoverUnseenInCache = (): void => {
    window.PMW.setCacheItem(CAPTION_TAGS_POPOVER_ALREADY_SHOWN, false);
  };

  const handlePopOverClose = (): void => {
    setShowTagsPopOver(false);
    void window.PMW.writeLocal('socialmedia/incrementUserHasSeenCaptionTagsPopoverCount');
  };

  useImperativeHandle(ref, () => ({
    onTextAreaClick(): void {
      const popOverHasAlreadyBeenShown = window.PMW.getCacheItem(CAPTION_TAGS_POPOVER_ALREADY_SHOWN) as boolean;

      if (!popOverHasAlreadyBeenShown) {
        setShowTagsPopOver(true);
      }
    },
  }));

  const renderPopoverContent = (): ReactElement => {
    const captionTagsHtml = window.i18next.t('pmwjs_caption_tags_info', {
      mentions: `<span class="${styles.blueText}">@mentions</span>`,
      hashtags: `<span class="${styles.blueText}">#hashtags</span>`,
    });

    return (
      <Popover
        referenceElementRef={textAreaContainerRef}
        popoverCloseFunction={() => {
          handlePopOverClose();
        }}
        alignment={PopoverDefaultAlignment.BOTTOM_START}
        popoverOffset={-180}
        hasArrow
        isPositionFixed={false}
        className={`border-s-standard ${styles.popOver}`}
        disableAutoPositioning={true}
      >
        <div className={`flex-v-row spacing-p-3`}>
          <div className={`flex-row-justify-between flex-align-items-center spacing-m-b-3`}>
            <Text size={TextSize.SMALL} bold val={window.i18next.t('pmwjs_add_tags_easily')} />
            <Icon onClick={handlePopOverClose} icon="icon-close" size={IconSize.SIZE_ICON_16} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} isUsedAsButton />
          </div>
          <div>
            <img src={getAssetUrl('caption-tags-popover-image.webp')} alt={'captions tooltip image'} />
          </div>
          <Text size={TextSize.XSMALL} className={'spacing-m-t-2'} val={captionTagsHtml} dangerouslySetInnerHTML />
        </div>
      </Popover>
    );
  };

  if (showTagsPopOver && isUserEligibleToSeeTagsPopOver) {
    window.PMW.setCacheItem(CAPTION_TAGS_POPOVER_ALREADY_SHOWN, true, window.PMW.CACHE_EXPIRY_HALF_HOUR);
    return renderPopoverContent();
  }

  return null;
});
